import React, { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { useSelector, useDispatch } from "react-redux";
import Modal, { ModalActions, ModalContent, ModalHeader, ModalTitle } from "../components/Modal";
import { Button } from "../components/Button";
import CreatableSelect from 'react-select/creatable';
import Ai, { selectAllNeuronVms, selectAllQuotas, selectAllVMs, selectCommunityVMs, selectRegionsFromInstances, selectTrainingTemplate } from '../store/models/Ai';
import { getVMOptions, generateRandomId, formatPrice, getErrorMsg, generateRandomIdWithPrefix, getVmResourcesString, convertUnit, checkIsBasicPlan, roundUpTo2Decimals, convertCommunityUnit } from '../utils';
import { selectCurrentProjectId } from '../store/models/Project';
import { CpuVms, HideFilterLevel, ModalTypes, NeuronImageOptions } from '../constants';
import { selectCurrentUser, selectCurrentUserId } from '../store/models/User';
import UIState, { selectMobileLayout } from '../store/UIState';
import { toast } from "react-toastify";
import Organization, { selectCurrentOrgId, selectIsFeatureEnabled } from '../store/models/Organization';
import Tooltip from '../components/Tooltip';
import { BackArrowIcon, QuestionMarkIcon } from '../components/Svg';
import CustomSelect from '../components/CustomSelect';
import AiStorage, { selectAllDetailedRegions, selectAllRegions, selectAllStorages } from '../store/models/AiStorage';
import { createSelector } from 'reselect';
import { useParams } from 'react-router-dom';
import Loader from '../components/Loader';
import StepBar from '../components/StepBar';
import classNames from 'classnames';
import { AiTab, AiTabs } from '../components/AiTabs';
import { Accordion, AccordionHeader } from '../components/Accordion';
import { CommunityNodeSearchBar } from '../components/CommunityNodeSearchBar';
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { IoMdArrowDown, IoMdArrowUp } from "react-icons/io";



const selectStyles = {
  container: (styles, { isDisabled }) => ({
    ...styles,
    flex: 1,
    height: 40,
    cursor: isDisabled ? 'not-allowed' : 'default',
  }),
  control: (styles, { isDisabled, ...state }) => ({
    ...styles,
    paddingLeft: 15,
    backgroundColor: '#191D29',
    borderColor: '#3D4463',
    borderRadius: 6,
    ':hover': {
      borderColor: 'white',
    },
    '.selected': {
      borderColor: 'white',
    },
    cursor: isDisabled ? 'not-allowed' : 'default',
    color: isDisabled ? '#636B91' : 'white',
  }),
  menu: (styles, state) => ({
    ...styles,
    border: '1px solid #3D4463',
    backgroundColor: '#191D29',
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: '#191D29',
      borderColor: '#3D4463',
      color: isDisabled ? '#636B91' : (isFocused ? 'white' : (isSelected ? 'white' : data?.value === 'create_new' ? '#18C99D' : '#636B91')),
      opacity: isDisabled ? 0.5 : 1,
    };
  },
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: '#8A8FB5',
  }),
  indicatorsContainer: (styles, { isDisabled }) => ({
    ...styles,
    display: isDisabled ? 'none' : 'flex',
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    color: '#636B91',
    backgroundColor: '#636B91',
    display: 'none'
  }),
  input: styles => ({ ...styles, color: "#fff" }),
  placeholder: styles => ({ ...styles, color: '#636B91' }),
  singleValue: (styles, { isDisabled, ...state }) => ({
    ...styles,
    color: isDisabled ? '#636B91' : 'white',
    cursor: isDisabled ? 'not-allowed' : 'default',
    fontWeight: '500'
  }),
};

const selectRegionsAndStorages = createSelector(
  selectAllRegions,
  selectAllStorages,
  (regions, storages) => ({
    regions: regions || [],
    storages: storages || {}
  })
);

export const ManageGpuNodeModal = ({ onRequestClose, deploymentId = null, shard = null, deployment }) => {
  const dispatch = useDispatch();
  const isEditing = !!deployment;

  const [loading, setLoading] = useState(false);
  const [loadingVMRegion, setLoadingVMRegion] = useState(false);
  const [errorVMRegion, setErrorVMRegion] = useState(null);
  const [error, setError] = useState(null);
  const [existingDeployment, setExistingDeployment] = useState(deployment);
  const [orderBy, setOrderBy] = useState({ label: 'vRAM size', value: 'vram' });

  const userId = useSelector(state => selectCurrentUserId(state));
  const curUser = useSelector(state => selectCurrentUser(state));
  const orgId = useSelector(state => selectCurrentOrgId(state));
  const projectId = useSelector(state => selectCurrentProjectId(state));
  const trainingTemplate = useSelector(state => selectTrainingTemplate(state));
  const vmRegions = useSelector(state => selectRegionsFromInstances(state));
  const detailedRegions = useSelector(state => selectAllDetailedRegions(state));
  const quotas = useSelector(state => selectAllQuotas(state));
  const isMobile = useSelector(state => selectMobileLayout(state));

  const [selectedTab, setSelectedTab] = useState('theta');
  const [imagesOptions, setImageOptions] = useState(selectedTab === 'neuron' ?
    NeuronImageOptions
    : trainingTemplate
      ? trainingTemplate.container_images.map(img => ({ label: img, value: img }))
      : []);
  let allVMs = useSelector(state => selectAllVMs(state));
  let VMs = {};
  let neuronVms = useSelector(state => selectAllNeuronVms(state));
  const hasNoNeuron = Object.keys(neuronVms).length === 0;
  Object.keys(allVMs).forEach(k => {
    let vm = allVMs[k];
    let include = true;
    if (vm.description) {
      try {
        let description = JSON.parse(vm.description);
        const filterLevel = _.get(description, 'hideFilterLevel') || 0;
        include = filterLevel < HideFilterLevel.neuron;
      } catch (e) {
        include = true;
      }
    }
    if (include) {
      VMs[k] = vm;
    } else if (hasNoNeuron) {
      neuronVms[k] = vm;
    }
  })
  let communityVMs = useSelector(state => selectCommunityVMs(state));
  communityVMs = Object.fromEntries(
    Object.entries(communityVMs)
      .sort(([, a], [, b]) => {
        const order = _.get(orderBy, 'value');
        if (order === 'price_dec') {
          return b.label_onthetaedgecloud_com_price_hr - a.label_onthetaedgecloud_com_price_hr;
        }
        if (order === 'price_inc') {
          return a.label_onthetaedgecloud_com_price_hr - b.label_onthetaedgecloud_com_price_hr;
        }
        if (order === 'mem') {
          return Number(b.memory_capacity) - Number(a.memory_capacity);
        }
        if (order === 'vram') {
          return Number(b.label_nvidia_com_gpu_memory) - Number(a.label_nvidia_com_gpu_memory);
        }
        if (order === 'disk') {
          return Number(b.ephemeral_storage_capacity) - Number(a.ephemeral_storage_capacity);
        }
        return 0;
      })
  );
  let [suggestedVM, setSuggestedVM] = useState(_.get(trainingTemplate, 'suggested_vms.0'));
  let [selectedVM, setSelectedVM] = useState({});
  let [selectedRegion, setSelectedRegion] = useState();
  let [vmOptions, setVmOptions] = useState(getVMOptions(VMs, suggestedVM));
  let defaultVM = vmOptions.filter(o => o.value === suggestedVM)[0] || vmOptions[0];
  let [selectedVMOption, setSelectedVMOption] = useState(defaultVM);
  let [price, setPrice] = useState(formatPrice(VMs[suggestedVM || vmOptions[0].value].price_hour / 100));
  const [imageValue, setImageValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [cVmLoading, setCVmLoading] = useState(false);
  const defaultName = generateRandomIdWithPrefix('gpunode', 20);
  const [maxHr, setMaxHr] = useState()

  const nameRef = useRef();
  const imageRef = useRef();
  const sshPortRef = useRef();
  const httpPortRef = useRef();
  const vmRef = useRef();
  const sshKeyRef = useRef();
  const regionRef = useRef();
  const mountPathRef = useRef();
  const maxHrRef = useRef();

  const { regions, storages } = useSelector(selectRegionsAndStorages);
  const [selectedStorage, setSelectedStorage] = useState(null);

  const [dname, setDname] = useState('');
  const [httpPort, setHttpPort] = useState('');
  const enableStorage = !_.isEmpty(vmRegions);
  const steps = [{ name: 'Machine Type' }, { name: 'Region' }, { name: 'GPU Node' }];
  const [activeStep, setActiveStep] = useState(isEditing ? 2 : 0);
  useEffect(() => {
    if (isEditing) setLoading(false);
  }, [isEditing])

  useEffect(() => {
    (async () => {
      try {
        setCVmLoading(true);
        await dispatch(Ai.actions.fetchCommunityVMs(projectId));
      } catch (e) {

      } finally {
        setCVmLoading(false);
      }
    })()
  }, [projectId])

  useEffect(() => {
    const fetchRegionsAndStorages = async () => {
      try {
        // Fetch regions if not already in the store
        if (regions.length === 0) {
          await dispatch(AiStorage.actions.fetchRegions());
        }
        // Fetch storages if not already in the store
        if (Object.keys(storages).length === 0) {
          await dispatch(AiStorage.actions.fetchStorages(projectId));
        }
      } catch (error) {
        console.error('Error fetching regions and storages:', error);
        toast.error('Failed to fetch regions and storages');
      }
    };

    if (!userId) return;
    fetchRegionsAndStorages();
  }, [projectId]);

  const handleInputChange = (newInputValue) => {
    setInputValue(newInputValue);
    return newInputValue;
  };

  const handleBlur = () => {
    if (inputValue) {
      // setValue();
      onImageChange({ label: inputValue, value: inputValue });
      setInputValue('');
    }
  };

  const onSubmit = async () => {
    if (_.isNil(userId)) {
      dispatch(UIState.actions.showModal(ModalTypes.SIGNUP))
      return;
    }
    if (!isEditing && !imageRef.current.state.selectValue[0]) {
      imageRef.current.focus();
      setError("Container image can't be empty.");
      return;
    }
    let name = nameRef.current.value;
    let image = !isEditing && _.get(imageRef, 'current.state.selectValue[0].value');
    let sshPort = Number(sshPortRef.current.value);
    if (isNaN(sshPort)) {
      sshPortRef.current.focus();
      setError("SSH port must be a number.");
      return;
    }
    if (!sshPort) {
      sshPortRef.current.focus();
      setError("SSH port can't be empty.");
      return;
    }
    let httpPort = Number(httpPortRef.current.value);
    if (isNaN(httpPort)) {
      httpPortRef.current.focus();
      setError("HTTP port must be a number.");
      return;
    }
    let vm = (selectedTab === 'theta' || selectedTab === 'neuron') ? selectedVM.id : selectedVM.vm_id;
    // let vm = vmRef.current.state.selectValue[0].value;
    // let pwd = pwdRef.current.value;
    let sshKey = sshKeyRef.current.value;
    if (!sshKey) {
      sshKeyRef.current.focus();
      setError("SSH key can't be empty.");
      return;
    }

    const imageId = trainingTemplate.id;
    const tags = trainingTemplate.tags;
    if (name.length) {
      if (!/^[A-Za-z0-9]+$/.test(name)) {
        const errorStr = 'Invalid ID. The ID should only contain letters and numbers.'
        setError(errorStr);
        toast.error('Error: ' + errorStr);
        return;
      }
      if (name.length > 20) {
        const errorStr = 'Invalid ID. The length of the ID should be less than or equal to 20.'
        setError(errorStr);
        toast.error('Error: ' + errorStr)
        return;
      }
    }
    let maxHrRate = ''
    if (maxHr != undefined) {
      maxHrRate = maxHr * 10000;
      if (maxHrRate < Number(selectedVM.label_onthetaedgecloud_com_price_hr)) {
        setError(`Max hourly rate can't be lower than current rate: $${roundUpTo2Decimals(selectedVM.label_onthetaedgecloud_com_price_hr / 10000)}.`);
        return;
      }
    }
    // let region = regionRef.current?.getValue()?.[0]?.value;
    let regionShard = _.get(detailedRegions.filter(o => o.region === selectedRegion), '0.shard');
    let persistentVolume = selectedStorage?.value;
    let mountPath = mountPathRef.current?.value;
    // if (!region) {
    //   regionRef.current.focus();
    //   setError("Region can't be empty.");
    //   return;
    // }

    setLoading(true);
    setError(null);
    try {
      const deploymentData = isEditing
        ? {
          project_id: projectId,
          annotations: {
            nickname: dname
          },
          volumes: persistentVolume ? [{
            volumeId: persistentVolume,
            region: selectedStorage.region,
            mountPath: mountPath || undefined,
          }] : undefined,
        }
        : {
          name: name,
          project_id: projectId,
          deployment_image_id: imageId,
          container_image: isEditing ? undefined : image || input,
          min_replicas: 1,
          max_replicas: 1,
          vm_id: vm,
          annotations: {
            tags: JSON.stringify(tags),
            nickname: dname
          },
          additional_ports: [sshPort],
          container_port: Number(httpPort),
          env_vars: {
            "SSH_PUBLIC_KEY": sshKey
          },
          require_env_vars: true,
          volumes: persistentVolume ? [{
            volumeId: persistentVolume,
            region: selectedStorage.region,
            mountPath: mountPath || undefined,
          }] : undefined,
          selected_region: selectedRegion,
          shard: selectedTab === 'theta' ? Number(regionShard) : Number(selectedVM.label_onthetaedgecloud_com_shard),
          price_hr: Math.ceil(maxHrRate) + ''
        };

      if (isEditing) {
        await dispatch(Ai.actions.updateDeployment(shard, deploymentId, deploymentData));
      } else {
        await dispatch(Ai.actions.createDeployment(deploymentData));
      }

      await dispatch(Ai.actions.fetchGpuNodes(projectId));
      onRequestClose();
    } catch (e) {
      let quotaId;
      try {
        quotaId = await dispatch(Organization.actions.fetchOrgQuotaId(orgId))
      } catch (e) {
        console.log('error in fetch quotaId:', e.message);
      }
      let error = getErrorMsg(e.message, onRequestClose, curUser, orgId, vm, checkIsBasicPlan(quotas, quotaId));
      setLoading(false);
      setError(error);
      if (error === e.message) {
        toast.error(e.message)
      }
    }
  }

  const onImageChange = (v) => {
    setImageValue(v);
    const index = trainingTemplate.container_images.findIndex(img => img === v.value);
    if (index > -1) {
      let port = _.get(trainingTemplate, 'container_port')
      setHttpPort(port !== 0 ? port : '');
    } else {
      setHttpPort('');
    }
  }

  const onSelectVolume = (selectedOption) => {
    if (selectedOption && selectedOption.value === 'create_new') {
      dispatch(UIState.actions.showModal(ModalTypes.MANAGE_STORAGE, { region: selectedRegion }));
      // Reset the select input
      setSelectedStorage(null);
    } else {
      setSelectedStorage(selectedOption);
    }
  };

  const handleSelectVM = async (vm, type) => {
    if (_.isNil(userId)) {
      dispatch(UIState.actions.showModal(ModalTypes.SIGNUP))
      return;
    }
    try {
      setLoadingVMRegion(true);
      setError();
      let s = 0;
      switch (type) {
        case 'theta':
          let regionRes;
          if (CpuVms.has(vm.id)) {
            regionRes = await dispatch(Ai.actions.fetchRegionsFromCpuInstances(projectId, vm.name))
          } else {
            regionRes = await dispatch(Ai.actions.fetchRegionsFromInstances(projectId, vm.name))
          }
          Object.keys(regionRes).forEach(regionKey => {
            let region = regionRes[regionKey];
            Object.keys(region).forEach(k => {
              if (k === vm.name) {
                s += region[k];
              }
            })
          })
          break;
        case 'community':
          s = 1;
          break;
        default:
          break;
      }
      if (s === 0) {
        setError('There are no machines of this type currently available. Please try again later. Thank you for your patience as we look into increasing the machines we have for this type.');
      } else {
        setSelectedVM(vm);
        let params = {};
        switch (type) {
          case 'theta':
            params.machine_type = vm.id;
            break;
          case 'community':
            params = {
              cuda_major: vm.label_nvidia_com_cuda_runtime_major,
              cuda_minor: vm.label_nvidia_com_cuda_runtime_minor,
              driver_major: vm.label_nvidia_com_cuda_driver_major,
              driver_minor: vm.label_nvidia_com_cuda_driver_minor,
              gpu_count: vm.label_nvidia_com_gpu_count,
              gpu_memory: vm.label_nvidia_com_gpu_memory,
              gpu_product: vm.label_nvidia_com_gpu_product
            }
            break;
          default:
            break;
        }
        let res = await dispatch(Ai.actions.fetchImagesByVMs(trainingTemplate.id, params));
        setImageOptions(res.map(o => ({
          label: o.container_image_uri,
          value: o.container_image_uri,
          is_compatible: o.is_compatible
        })))
        setActiveStep(v => v + 1);
      }
      setLoadingVMRegion(false);
    } catch (e) {
      console.log('handleNext error:', e.message);
      setLoadingVMRegion(false);
    }
  }

  useEffect(() => {
    if (selectedVM.label_onthetaedgecloud_com_price_hr) {
      setMaxHr(roundUpTo2Decimals(selectedVM.label_onthetaedgecloud_com_price_hr / 10000))
    }
  }, [selectedVM.label_onthetaedgecloud_com_price_hr])

  const handleMaxHrChange = (e) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setMaxHr(value);
    }
  }

  return (
    <Modal className={cx("ManageGpuNodeModal")}
      onRequestClose={onRequestClose}>

      <ModalHeader>
        <ModalTitle align={'row'}>
          {isEditing ? 'Edit' : 'Create New'} GPU Node with SSH Access
          <Tooltip tooltip={'Open documentation'}>
            <a href="https://docs.thetatoken.org/docs/edgecloud-ai-training-with-gpu-nodes" target='_blank'>
              <QuestionMarkIcon />
            </a>
          </Tooltip>
        </ModalTitle>
      </ModalHeader>
      {activeStep !== 0 && <div className='modal-back-button' onClick={() => {
        if (activeStep === 1) setSelectedVM({});
        if (activeStep === 2) setSelectedRegion();
        setImageValue(null);
        setActiveStep(v => v - 1)
      }}>
        <BackArrowIcon />
        <span>Back</span>
      </div>}

      {(false)
        ? <Loader />
        : <>
          <div className='ManageGpuNodeModal__row'>
            <StepBar steps={(selectedTab === 'theta' || selectedTab === 'neuron')
              ? steps
              : [steps[0], steps[2]]}
              activeStep={activeStep}
              className={'ManageGpuNodeModalStepBar'} />
          </div>
          {!isEditing && <div className='ManageGpuNodeModal__row'>
            {activeStep === 0 && <div className='ManageGpuNodeModal__step'>
              Step 1. Select the Machine Type
              <AiTabs>
                <AiTab isSelected={(selectedTab === 'theta')}
                  onClick={() => {
                    setSelectedTab('theta');
                  }}>
                  Hosted GPUs
                </AiTab>
                <AiTab isSelected={(selectedTab === 'community')}
                  onClick={() => {
                    setSelectedTab('community');
                  }}>
                  Community GPUs
                </AiTab>
                <AiTab className="ManageGpuNodeModal__tab"
                  isSelected={(selectedTab === 'neuron')}
                  onClick={() => {
                    setSelectedTab('neuron');
                  }}>
                  AWS Neuron
                  <Tooltip tooltip={'AWS Trainium and Inferentia'}>
                    <QuestionMarkIcon />
                  </Tooltip>
                </AiTab>
              </AiTabs>
            </div>}
            {(activeStep === 1 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              && <div className='ManageGpuNodeModal__step'>
                Step 2. Select the Region
                <div className='ManageGpuNodeModal__step--info'>
                  {(selectedTab === 'theta' || selectedTab === 'neuron')
                    ? `Machine Type: ${selectedVM.name} (${getVmResourcesString(selectedVM.resources)}) $${selectedVM.price_hour / 100}/hr`
                    : `Machine Type: ${selectedVM.gpu_allocate} x ${selectedVM.label_nvidia_com_gpu_product} ${convertUnit(selectedVM.label_nvidia_com_gpu_memory, 'mb', undefined, 0)} $${roundUpTo2Decimals(selectedVM.label_onthetaedgecloud_com_price_hr / 10000)}/hr`
                  }
                </div>
              </div>}
            {((activeStep === 2 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              || (activeStep === 1 && selectedTab === 'community'))
              && <div className='ManageGpuNodeModal__step'>
                Step {activeStep + 1}. Configure the GPU Node
                <div className='ManageGpuNodeModal__step--info'>
                  {(selectedTab === 'theta' || selectedTab === 'neuron')
                    ? `Machine Type: ${selectedVM.name} (${getVmResourcesString(selectedVM.resources)}) $${selectedVM.price_hour / 100}/hr`
                    : `Machine Type: ${selectedVM.gpu_allocate} x ${selectedVM.label_nvidia_com_gpu_product} ${convertUnit(selectedVM.label_nvidia_com_gpu_memory, 'mb', undefined, 0)} $${roundUpTo2Decimals(selectedVM.label_onthetaedgecloud_com_price_hr / 10000)}/hr`
                  }
                </div>
                {selectedRegion && <div className='ManageGpuNodeModal__step--info'>
                  {`Region: ${selectedRegion}`}
                </div>}
              </div>}
          </div>}
          <ModalContent className={classNames(`ManageGpuNode`, { 'visable': activeStep === 2 })}>
            {activeStep === 0 && <>
              {selectedTab === 'theta' && (!loadingVMRegion ?
                Object.keys(VMs).map((k, i) => {
                  const vm = VMs[k];
                  const hasGpu = vm.resources.gpu;
                  return <div className='ManageGpuNodeModal__row' key={i}>
                    <div className={classNames('ManageGpuNodeModal__vm-button', { 'active': vm.id === selectedVM.id })}
                      onClick={() => { handleSelectVM(vm, 'theta') }}>
                      <div className='ManageGpuNodeModal__vm-button--left'>
                        <div className='ManageGpuNodeModal__vm-button--title'>
                          <div className='ManageGpuNodeModal__vm-button--title-name'>
                            <div className='ManageGpuNodeModal__vm-button--name'>{`${vm.name}`}</div>
                            {hasGpu && <div className='ManageGpuNodeModal__vm-button--gpu'>{` (${vm.resources['gpu_model']} x ${vm.resources.gpu})`}</div>}
                          </div>
                          {isMobile && <div className='ManageGpuNodeModal__vm-button--title-price'>
                            {`$${(vm.price_hour / 100)}/hour`}
                          </div>}
                        </div>
                        <div className='ManageGpuNodeModal__vm-button--subtitle'>
                          {`CPU: ${vm.resources.cpu / 1000} Cores, Mem: ${convertUnit(vm.resources.mem, 'MB')}, Ephemeral storage: ${convertUnit(vm.resources.storage, 'GB')}`}
                        </div>
                      </div>
                      {!isMobile && <div className='ManageGpuNodeModal__vm-button--right'>
                        <div className='ManageGpuNodeModal__vm-button--price'>{`$${(vm.price_hour / 100)} per hour`}</div>
                      </div>}
                    </div>
                  </div>
                }) : <Loader />)}
              {selectedTab === 'neuron' && (!loadingVMRegion ?
                Object.keys(neuronVms).map((k, i) => {
                  const vm = neuronVms[k];
                  const hasGpu = vm.resources.gpu;
                  return <div className='ManageGpuNodeModal__row' key={i}>
                    <div className={classNames('ManageGpuNodeModal__vm-button', { 'active': vm.id === selectedVM.id })}
                      onClick={() => { handleSelectVM(vm, 'theta') }}>
                      <div className='ManageGpuNodeModal__vm-button--left'>
                        <div className='ManageGpuNodeModal__vm-button--title'>
                          <div className='ManageGpuNodeModal__vm-button--title-name'>
                            <div className='ManageGpuNodeModal__vm-button--name'>{`${vm.name}`}</div>
                            {hasGpu && <div className='ManageGpuNodeModal__vm-button--gpu'>{` (${vm.resources['gpu_model']} x ${vm.resources.gpu})`}</div>}
                          </div>
                          {isMobile && <div className='ManageGpuNodeModal__vm-button--title-price'>
                            {`$${(vm.price_hour / 100)}/hour`}
                          </div>}
                        </div>
                        <div className='ManageGpuNodeModal__vm-button--subtitle'>
                          {`CPU: ${vm.resources.cpu / 1000} Cores, Mem: ${convertUnit(vm.resources.mem, 'MB')}, Ephemeral storage: ${convertUnit(vm.resources.storage, 'GB')}`}
                        </div>
                      </div>
                      {!isMobile && <div className='ManageGpuNodeModal__vm-button--right'>
                        <div className='ManageGpuNodeModal__vm-button--price'>{`$${(vm.price_hour / 100)} per hour`}</div>
                      </div>}
                    </div>
                  </div>
                }) : <Loader />)}
              {selectedTab === 'community' && (!cVmLoading ?
                <>
                  <CommunityNodeSearchBar orderBy={orderBy} setOrderBy={setOrderBy} loading={cVmLoading} setLoading={setCVmLoading} />
                  {Object.keys(communityVMs).length ? Object.keys(communityVMs).map((k, i) => {
                    const vm = communityVMs[k];
                    const units = ['BYTES', 'KB', 'MB', 'GB', 'TB'];
                    const countryInfo = vm.label_onthetaedgecloud_com_country ? `${vm.label_onthetaedgecloud_com_country.replaceAll('_', ' ')}` : '';
                    const provinceInfo = vm.label_onthetaedgecloud_com_province ? `${vm.label_onthetaedgecloud_com_province.replaceAll('_', ' ')}` : '';
                    const regionInfo = countryInfo ? `Location: ${provinceInfo ? `${provinceInfo}, ` : ''}${countryInfo}` : '';
                    return <div className='ManageGpuNodeModal__row' key={i}>
                      <div className={classNames('ManageGpuNodeModal__vm-button', { 'active': vm.id === selectedVM.id })}
                        onClick={() => { handleSelectVM(vm, 'community') }}>
                        <div className='ManageGpuNodeModal__vm-button--left'>
                          <div className='ManageGpuNodeModal__vm-button--title'>
                            <div className='ManageGpuNodeModal__vm-button--title-name'>
                              <div className='ManageGpuNodeModal__vm-button--gpu'>{`${vm.gpu_allocate} x ${vm.label_nvidia_com_gpu_product} ${convertUnit(vm.label_nvidia_com_gpu_memory, 'mb', units, 0)}`}</div>
                            </div>
                            {isMobile && <div className='ManageGpuNodeModal__vm-button--title-price'>
                              {`$${roundUpTo2Decimals(vm.label_onthetaedgecloud_com_price_hr / 10000)}/hour`}
                            </div>}
                          </div>
                          <div className='ManageGpuNodeModal__vm-button--subtitle'>
                            <div className='ManageGpuNodeModal__vm-button--cpu'>
                              {`CPU: ${vm.cpu_allocate} Cores, Mem: ${convertCommunityUnit(vm.memory_allocate, vm.memory_capacity, 'bytes', units, 0)}, Ephemeral storage: ${convertCommunityUnit(vm.ephemeral_storage_allocate, vm.ephemeral_storage_capacity, 'bytes', units, 0)}, Max Cuda: ${vm.label_nvidia_com_cuda_runtime_major}.${vm.label_nvidia_com_cuda_runtime_minor}`}
                              <IoMdArrowUp />{vm.label_onthetaedgecloud_com_upload_speed_mbps}Mbps
                              <IoMdArrowDown />{vm.label_onthetaedgecloud_com_download_speed_mbps}Mbps
                            </div>
                          </div>
                          {regionInfo && <div className='ManageGpuNodeModal__vm-button--subtitle'>
                            <div className='ManageGpuNodeModal__vm-button--cpu'>{regionInfo}</div>
                          </div>}
                          <div className='ManageGpuNodeModal__vm-button--subtitle'>
                            <div className='ManageGpuNodeModal__vm-button--name'>{`ID: ${vm.vm_id}`}</div>
                          </div>
                        </div>
                        {!isMobile && <div className='ManageGpuNodeModal__vm-button--right'>
                          <div className='ManageGpuNodeModal__vm-button--price'>{`$${roundUpTo2Decimals(vm.label_onthetaedgecloud_com_price_hr / 10000)} per hour`}</div>
                        </div>}
                      </div>
                    </div>
                  }) : 'No community node available'}
                </> : <Loader />)}
            </>}
            {(activeStep === 1 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              && <>
                {detailedRegions.map((region, i) => {
                  const disabled = Object.keys(vmRegions).indexOf(region.region) < 0;
                  return <div className='ManageGpuNodeModal__row' key={i}>
                    <div className={classNames('ManageGpuNodeModal__vm-button',
                      {
                        'active': region.region === selectedRegion,
                        'disabled': disabled
                      })}
                      onClick={() => {
                        if (disabled) return;
                        setSelectedRegion(region.region)
                        setActiveStep(2);
                      }}>
                      <div className='ManageGpuNodeModal__vm-button--title region'>
                        <div className='ManageGpuNodeModal__vm-button--name'>{region.region}</div>
                      </div>
                    </div>
                  </div>
                })}
              </>}
            {((activeStep === 2 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              || (activeStep === 1 && selectedTab === 'community'))
              && <>
                <div className='ManageGpuNodeModal__row'>
                  <div className='ManageGpuNodeModal__select-wrap name'>
                    <div className='ManageGpuNodeModal__select-label'>GPU Node ID</div>
                    <input className={"ManageGpuNodeModal__input"}
                      placeholder={'Enter gpu node ID'}
                      ref={nameRef}
                      defaultValue={isEditing ? existingDeployment.Suffix : defaultName}
                      disabled={true}
                    />
                  </div>
                  <div className='ManageGpuNodeModal__select-wrap name'>
                    <div className='ManageGpuNodeModal__select-label'>GPU Node Name&nbsp;<div className='text-optional'>(optional)</div></div>
                    <input className={"ManageGpuNodeModal__input"}
                      placeholder={'Enter gpu node name'}
                      value={dname}
                      onChange={(e) => setDname(e.target.value)}
                    />
                  </div>
                </div>
                <div className='ManageGpuNodeModal__row'>
                  <div className='ManageGpuNodeModal__select-wrap image'>
                    <div className='ManageGpuNodeModal__select-label'>GPU Node Image</div>
                    {isEditing ? <div>{imageValue}</div> : <CreatableSelect
                      options={imagesOptions}
                      className={"ManageGpuNodeModal__selector"}
                      placeholder={"Container image with sshd, e.g. thetalabsorg/ubuntu-sshd:latest"}
                      styles={selectStyles}
                      onChange={onImageChange}
                      ref={imageRef}
                      isDisabled={isEditing}
                      onInputChange={handleInputChange}
                      onBlur={handleBlur}
                      value={imageValue}
                      inputValue={inputValue}
                      isOptionDisabled={(option) => {
                        if (option.is_compatible === undefined) return false;
                        return !option.is_compatible;
                      }}
                    />}
                  </div>
                </div>
                <div className='ManageGpuNodeModal__row'>
                  <div className='ManageGpuNodeModal__select-wrap ports'>
                    <div className='ManageGpuNodeModal__select-label'>SSH Port</div>
                    <input className={"ManageGpuNodeModal__input"}
                      placeholder={'Enter SSH port'}
                      ref={sshPortRef}
                      defaultValue={isEditing ? existingDeployment.AdditionalPorts[0] : 22}
                      disabled={isEditing}
                    />
                    <div className='ManageGpuNodeModal__select-label'>HTTP Port&nbsp;<div className='text-optional'>(optional)</div></div>
                    <input className={"ManageGpuNodeModal__input"}
                      placeholder={'Enter HTTP port'}
                      ref={httpPortRef}
                      value={httpPort}
                      defaultValue={isEditing ? existingDeployment.ContainerPort : ''}
                      disabled={isEditing}
                      onChange={(e) => setHttpPort(e.target.value)}
                    />
                  </div>
                  <div className='ManageGpuNodeModal__select-wrap ssh'>
                    <div className='ManageGpuNodeModal__select-label'>SSH Public Key. How to&nbsp;<a href="https://git-scm.com/book/en/v2/Git-on-the-Server-Generating-Your-SSH-Public-Key"
                      target='_blank'>generate a public key.</a></div>
                    <textarea className={"ManageGpuNodeModal__textarea"}
                      placeholder={'ssh-rsa AAAAB...... (content of your ~/.ssh/id_rsa.pub file)'}
                      ref={sshKeyRef}
                      defaultValue={isEditing ? existingDeployment.EnvVars?.SSH_PUBLIC_KEY : ''}
                      disabled={isEditing}
                    />
                  </div>
                </div>
                {enableStorage && <div className='ManageGpuNodeModal__row last'>
                  <div className='ManageGpuNodeModal__select-wrap persistent-volume'>
                    <div className='ManageGpuNodeModal__select-label'>Persistent Volume (optional)</div>
                    <CustomSelect
                      options={[
                        ...Object.values(storages).map(storage => ({
                          value: storage.Suffix,
                          label: `${storage.DisplayName}  |  ${storage.Name}  |  ${storage.Region}`,
                          region: storage.Region,
                          isDisabled: selectedRegion !== storage.Region
                        })),
                        { value: 'create_new', label: "+ Create new volume" }
                      ]}
                      className={"ManageGpuNodeModal__selector"}
                      styles={selectStyles}
                      placeholder={'Select Persistent Volume'}
                      value={selectedStorage}
                      onChange={onSelectVolume}
                      isClearable
                    />
                  </div>
                  <div className='ManageGpuNodeModal__select-wrap mount-path'>
                    <div className='ManageGpuNodeModal__select-label'>Mounted to (optional)</div>
                    <input
                      className={"ManageGpuNodeModal__input"}
                      placeholder={'Enter mount path'}
                      ref={mountPathRef}
                      defaultValue={isEditing ? existingDeployment.volumes?.[0]?.mountPath : '/mnt/data1'}
                    />
                  </div>
                </div>}
                {selectedTab === 'community' && <Accordion
                  header={<AccordionHeader
                    title="Advanced Options"
                  />}
                  body={<>
                    <div className='CreateDeploymentModal__row'>
                      <div className='CreateDeploymentModal__section-wrap name'>
                        <div className='CreateDeploymentModal__select-label'>Max Hourly Rate ($)</div>
                        <div className='CreateDeploymentModal__row-wrap'>
                          <input className={"ManageGpuNodeModal__input"}
                            placeholder={'Set Max Hourly Rate'}
                            value={maxHr}
                            type="number"
                            onChange={handleMaxHrChange}
                            step={0.01}
                          />
                        </div>
                      </div>
                      <div className='CreateDeploymentModal__section-wrap name'></div>
                    </div>
                  </>} />}
              </>}
          </ModalContent >
          {error && <div className={cx("ManageGpuNodeModal__error")}>{error}</div>}
          <ModalActions>
            {activeStep === 0
              && <Button color={"transparent"} onClick={onRequestClose} title={"Cancel"} />}
            {(activeStep === 1 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              && <Button color={"transparent"} onClick={onRequestClose} title={"Cancel"} />}
            {((activeStep === 2 && (selectedTab === 'theta' || selectedTab === 'neuron'))
              || (activeStep === 1 && selectedTab === 'community'))
              && <>
                <Button color={"green"} onClick={onSubmit} title={isEditing ? "Update GPU Node" : "Create GPU Node"} loading={loading} />
                <Button color={"transparent"} onClick={onRequestClose} title={"Cancel"} />
              </>}
          </ModalActions>
        </>}

    </Modal >
  );
}